import { type FC } from "react";
import { Section, Spacing, Button } from "@snapchat/snap-design-system";
import styled from "@emotion/styled";
import { sectionTitleStyle, sectionSubtitleStyle, CopyToClipboardButton, Pipe } from "../../../common/components";
import { VendorDataCollection } from "../../../vendor-data/models";
import { type VendorDataChangedAction } from "../../../vendor-data";
import { Flexbox } from "../../../core/components";
import { LensPreview } from "../../components/LensPreview";
import { VendorDataSection } from "../../../vendor-data/components";
import { sectionStyle, vendorDataStyle } from "../../../common/styles/Section.styles";
import { lensIdIsCopiedToClipboard } from "../../../common/model";
import { formatMediumDateTime } from "../../../common/formatters";
import { type Permissions } from "../../../gql-generated/aliases";
import { LensModel } from "../models";
import { DisabledButtonToolipFix, ActionDisabledTooltip } from "../../../common/components/ActionDisabledTooltip";

const AddToLensGroupButton = styled(DisabledButtonToolipFix)`
    /* gives the button enough room to not be cut off */
    margin-right: 2px;
    margin-left: ${Spacing.SM}px;
`;

interface LensInfoSectionProps {
    lens: LensModel;
    saveIsDisabled: boolean;
    saveInProgress: boolean;
    globalVendorData: VendorDataCollection;
    onSave: () => void;
    onGlobalVendorDataChanged: (event: VendorDataChangedAction) => void;
    onShowAddToGroupModal: () => void;
    permissions: Permissions;
}

export const LensInfoSection: FC<LensInfoSectionProps> = ({
    lens,
    saveIsDisabled,
    saveInProgress,
    onSave,
    globalVendorData,
    onGlobalVendorDataChanged,
    onShowAddToGroupModal,
    permissions,
}) => (
    <Section
        title={
            <span className={sectionTitleStyle} data-testid="lens-title">
                {lens.name}
            </span>
        }
        rightHeaderContent={
            <Flexbox>
                <Button
                    onClick={onSave}
                    isDisabled={saveIsDisabled}
                    isPending={saveInProgress}
                    data-testid="save-changes-button"
                >
                    Save Changes
                </Button>
                <ActionDisabledTooltip
                    placement="left"
                    hasPermission={permissions.updateGroups!}
                    hasMusic={lens.hasMusic}
                >
                    <AddToLensGroupButton
                        onClick={onShowAddToGroupModal}
                        type="primary"
                        isDisabled={!permissions.updateGroups || lens.hasMusic}
                    >
                        Add To Lens Group
                    </AddToLensGroupButton>
                </ActionDisabledTooltip>
            </Flexbox>
        }
        subtitle={
            <Flexbox className={sectionSubtitleStyle}>
                Last Updated: {formatMediumDateTime(lens.updatedAt as Date)}
                <Pipe />
                Lens ID: {lens.compoundId.id}
                <CopyToClipboardButton
                    copyValue={lens.compoundId.id as string}
                    notification={lensIdIsCopiedToClipboard}
                    tooltip="Copy Lens ID"
                />
            </Flexbox>
        }
        className={sectionStyle}
    >
        <Flexbox>
            <LensPreview
                iconUrl={lens.iconUrl as string}
                videoUrl={lens.videoUrl}
                previewUrl={lens.previewWebpUrl}
                source={lens.lensSource}
            />
            <VendorDataSection
                placement="bottom"
                hasPermission={permissions.updateLenses!}
                disabled={!permissions.updateLenses}
                className={vendorDataStyle}
                vendorDataCollection={globalVendorData}
                onVendorDataChanged={onGlobalVendorDataChanged}
            />
        </Flexbox>
    </Section>
);
